import { AxiosResponse } from 'axios'

import { ServiceConfig } from 'shared/services/ServiceConfig'
import IAnttRequest from 'modules/user/domain/entities/AnttRequest'
import ICreatePasswordRequest from 'modules/user/domain/entities/CreatePasswordRequest'
import IDadosUsuarioResponse from 'modules/user/domain/entities/DadosUsuario'
import IFindAllFrotistaRequest from 'modules/user/domain/entities/FindAllFrotistaRequest'
import IFindAllFrotistaResponse from 'modules/user/domain/entities/FindAllFrotistaResponse'
import IUpdateUsuarioRequest from 'modules/user/domain/entities/IUpdateUsuarioRequest'
import IValidateRequest from 'modules/user/domain/entities/ValidateRequest'
import VerifyCnpjAvailabilityRequest from 'modules/user/domain/entities/VerifyCnpjAvailabilityRequest'
import VerifyCodeRequest from 'modules/user/domain/entities/VerifyCodeRequest'
import VerifyPhoneAndCnpjRequest from 'modules/user/domain/entities/VerifyPhoneAndCnpjRequest'
import IVerifyPhoneAndCnpjResponse from 'modules/user/domain/entities/VerifyPhoneAndCnpjResponse'
import VincularCNPJRequest from 'modules/user/domain/entities/VincularCNPJRequest'
import { ICreateUsuarioRequest } from 'modules/user/domain/usecases/CreateUsuarioImpl'

import IConsultZipCodeRequest from '../../domain/entities/ConsultZipCodeRequest'
import IConsultZipCodeResponse from '../../domain/entities/ConsultZipCodeResponse'
import ICreateFrotistaRequest from '../../domain/entities/CreateFrotistaRequest'
import ICreateFrotistaResponse from '../../domain/entities/CreateFrotistaResponse'
import IDataFilterRequest from '../../domain/entities/DataFilterRequest'
import IFindFilterResponse from '../../domain/entities/FindFilterResponse'
import INewPasswordRequest from '../../domain/entities/NewPasswordRequest'
import INewPasswordResponse from '../../domain/entities/NewPasswordResponse'
import IResetPasswordRequest from '../../domain/entities/ResetPasswordRequest'
import IResetPasswordResponse from '../../domain/entities/ResetPasswordResponse'
import ISendPinRequest from '../../domain/entities/SendPinRequest'
import IUpdateEmailRequest from '../../domain/entities/UpdateEmailRequest'
import IVerifyPinRequest from '../../domain/entities/VerifyPinRequest'
import IVerifyPinResponse from '../../domain/entities/VerifyPinResponse'
import { IUserRepository } from '../../domain/repositories/IUserRepository'
import ChangeGestorRequest from 'modules/user/domain/entities/ChangeGestorRequest'
import ChangeGestorResponse from 'modules/user/domain/entities/ChangeGestorResponse'
import IValidateAnttResponse from 'modules/user/domain/entities/IValidateAnttResponse'

const http = new ServiceConfig()
export default class UserRepository implements IUserRepository {
  public async findAllFrotistas({
    nome,
    page,
    per_page,
    active,
  }: IFindAllFrotistaRequest): Promise<IFindAllFrotistaResponse> {
    const response = await http.get('/frotistas', {
      params: {
        nome: nome && nome,
        page,
        per_page,
        active: active && active,
      },
    })

    return response.data as IFindAllFrotistaResponse
  }

  public async findAllUsuario(nome: string): Promise<IDadosUsuarioResponse[]> {
    const result = await http.get<
      IDadosUsuarioResponse[],
      {
        data: IDadosUsuarioResponse[]
      }
    >(`/usuarios?nome=${nome}`)

    return result.data
  }

  public async findByFrotistaId(
    frotistaId: number,
  ): Promise<IDadosUsuarioResponse> {
    const response = await http.get('/usuarios/dados', {
      params: {
        frotista_id: frotistaId,
      },
    })
    return response.data as IDadosUsuarioResponse
  }

  public async validateEmail(email: string): Promise<void> {
    await http.get('/usuarios/validations', {
      params: {
        email,
        type: 'ALREADY_EXISTS_AND_SEND_COD',
      },
    })
  }

  public async validateEmailCode(email: string, code: string): Promise<void> {
    await http.get('/usuarios/validations', {
      params: {
        code,
        email,
        type: 'EMAIL_CHECK_CODE',
      },
    })
  }

  public async validateAntt(
    params: IAnttRequest,
  ): Promise<IValidateAnttResponse> {
    const response = await http.get('/frotistas/check/antt-availability', {
      params: {
        antt: params?.antt,
        cnpj: params?.cnpj,
      },
    })

    return response.data as IValidateAnttResponse
  }

  public async findByFilter(
    params: IDataFilterRequest,
  ): Promise<IFindFilterResponse[]> {
    const response: AxiosResponse<IFindFilterResponse[]> = await http.get(
      '/frotistas',
      {
        params: {
          email: params?.email,
          antt: params?.antt,
        },
      },
    )

    return response.data
  }

  public async sendPin(params: ISendPinRequest): Promise<void> {
    await http.post('/telefone/send-pin', {
      phone: params.phone,
    })
  }

  public async verifyPin(
    params: IVerifyPinRequest,
  ): Promise<IVerifyPinResponse> {
    const response: AxiosResponse<IVerifyPinResponse> = await http.post(
      '/telefone/verify-pin',
      {
        phone: params.phone,
        code: params.code,
      },
    )

    return response.data
  }

  public async consultZipCode(
    params: IConsultZipCodeRequest,
  ): Promise<IConsultZipCodeResponse> {
    const response: AxiosResponse<IConsultZipCodeResponse> = await http.get(
      `/cep/${params.cep}`,
    )

    return response.data
  }

  public async createFrotista({
    cnpj,
    doc_imagem_frente,
    doc_imagem_verso,
    doc_token_sdk,
    email,
    endereco,
    telefone,
    antt,
    senha,
  }: ICreateFrotistaRequest): Promise<ICreateFrotistaResponse> {
    const response: AxiosResponse<ICreateFrotistaResponse> = await http.post(
      '/signup',
      {
        cnpj,
        doc_imagem_frente,
        doc_imagem_verso,
        doc_token_sdk,
        email,
        endereco,
        telefone,
        antt,
        senha,
      },
    )

    return response.data
  }

  public async resetPassword({
    email,
  }: IResetPasswordRequest): Promise<IResetPasswordResponse> {
    const response: AxiosResponse<IResetPasswordResponse> = await http.put(
      '/auth/reset-password',
      { email },
    )

    return response.data
  }

  public async newPassword({
    email,
    hashAutenticator,
    newPassword,
  }: INewPasswordRequest): Promise<INewPasswordResponse> {
    const response: AxiosResponse<INewPasswordResponse> = await http.put(
      '/auth/new-password',
      { email, newPassword, hashAutenticator },
    )

    return response.data
  }

  public async createPassword({
    password,
    hashAutenticator,
  }: ICreatePasswordRequest): Promise<IResetPasswordResponse> {
    const response: AxiosResponse<INewPasswordResponse> = await http.post(
      '/auth/password',
      { password, hashAutenticator },
    )

    return response.data
  }

  public async verifyPhoneAndCnpj({
    phone,
    cnpj,
  }: VerifyPhoneAndCnpjRequest): Promise<IVerifyPhoneAndCnpjResponse> {
    let type = 'phone'
    if (cnpj) {
      type = 'phoneAndCnpj'
    }
    const response: AxiosResponse<Promise<IVerifyPhoneAndCnpjResponse>> =
      await http.get('/usuarios/verify', {
        params: {
          type,
          phone,
          cnpj,
        },
      })

    return response.data
  }

  public async verifyCode(params: VerifyCodeRequest): Promise<void> {
    await http.get('/usuarios/verify', {
      params: {
        type: 'code',
        code: String(params.code),
        usuario_id: params.usuario_id,
      },
    })
  }

  public async updateEmail(params: IUpdateEmailRequest): Promise<void> {
    await http.patch('/usuarios/update-email', {
      code: String(params.code),
      usuario_id: params.usuario_id,
      email: params.email,
    })
  }

  public async validate(params: IValidateRequest): Promise<void> {
    await http.get('/frotistas/validate', {
      params: params,
    })
  }

  public async verifyCnpjAvailability(
    params: VerifyCnpjAvailabilityRequest,
  ): Promise<void> {
    await http.get('/frotistas/check/cnpj-availability', {
      params: params,
    })
  }

  public async vincularCnpj(params: VincularCNPJRequest): Promise<void> {
    await http.post('/frotistas', params)
  }

  public async createUsuario(params: ICreateUsuarioRequest): Promise<void> {
    await http.post('/usuarios', params)
  }

  public async updateUsuario(
    id: number,
    params: IUpdateUsuarioRequest,
  ): Promise<void> {
    await http.put(`/usuarios/${id}`, params)
  }

  public async validateRequisitosViagem(frotista_id: number): Promise<any> {
    return await http.get(
      `/usuarios/validations/requisitos-viagem/${frotista_id}`,
    )
  }

  public async changeGestor(
    params: ChangeGestorRequest,
  ): Promise<ChangeGestorResponse> {
    const response: AxiosResponse<ChangeGestorResponse> = await http.put(
      '/gestores/substituir',
      params,
    )

    return response.data
  }

  public async needResendDocument(
    protocolo_ocr: string,
  ): Promise<{ needResend: boolean }> {
    const response: AxiosResponse<{ needResend: boolean }> = await http.get(
      `/usuarios/need-resend-document/${protocolo_ocr}`,
    )
    return response.data
  }

  public async resendDocument(params: {
    old_protocolo_ocr: string
    doc_imagem_frente: string
    doc_imagem_verso: string
  }): Promise<void> {
    await http.post('/usuarios/resend-document', params)
  }
}
